import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import { formatCurrency, useAppConfig, usePermission } from "../../../shared";
import { errorMessage } from "../../../utils";
import { cancelAndReissueFtiApi, getFtiDetailsApi } from "../apis";
import { useFtiPdfExport } from "./useFtiPdfExport";

export const useFullTaxInvoiceDetails = () => {
  const { ftiId } = useParams();
  const navigate = useNavigate();
  const { pathname, state: locationState } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const basePath = isStoreAdmin ? "/store-admin" : "";
  const { currencyPrecision } = useAppConfig();
  const isEditable = usePermission("fti-modify");
  const { isGenerating, generateFtiPdf, hasGeneratedPdf, deliverGeneratedPdf } =
    useFtiPdfExport();
  const [state, setState] = useImmer({
    details: null,
    isLoading: true,
    isReissueOpen: false,
    isReprintOpen: false,
    // What the "Download or Print?" dialog is open for: "new_fti" | "reprint" | null.
    // Either way the FTI / reprint is already generated, so a choice is required
    outputFor: null,
  });

  const breadcrumbs = [
    { name: "Dashboard", href: basePath || "/" },
    { name: "Full Tax Invoices", href: `${basePath}/full-tax-invoice` },
    { name: state.details?.fti_invoice_number ?? "" },
  ];

  const details = state.details ?? {};
  const reissueInitialValues = {
    business_name: details.business_name ?? "",
    business_branch: details.business_branch ?? "",
    full_business_address: details.full_business_address ?? "",
    business_state: details.business_state ?? "",
    business_postcode: details.business_postcode ?? "",
    abn_number: details.abn_number ?? "",
    business_country_code: details.country_code ?? "",
    business_phone: details.phone ?? "",
  };

  const reprintRemarks = `ใบแทนใบกำกับภาษีออกให้ครั้งที่ ${
    (details.rcipet_history?.length ?? 0) + 1
  } เนื่องจากต้นฉบับสูญหาย`;

  const getDetails = async (id, showLoader = true) => {
    setState((draft) => {
      draft.isLoading = showLoader;
    });
    try {
      const response = await getFtiDetailsApi(id);
      const { success, message, data } = response;
      if (success) {
        setState((draft) => {
          draft.details = data;
        });
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response?.data?.message ?? "Failed to fetch full tax invoice";
      errorMessage(message);
    } finally {
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const toggleReprint = (isReprintOpen) => {
    setState((draft) => {
      draft.isReprintOpen = isReprintOpen;
    });
  };

  const setOutputFor = (outputFor) => {
    setState((draft) => {
      draft.outputFor = outputFor;
    });
  };

  // GENERATE exports (and so records) the reprint first, then asks download or print.
  // On failure the reprint dialog stays open to retry
  const handleConfirmReprint = async () => {
    const isGenerated = await generateFtiPdf(ftiId);
    if (!isGenerated) return;
    toggleReprint(false);
    setOutputFor("reprint");
    getDetails(ftiId, false);
  };

  const handlePrint = (action) => {
    deliverGeneratedPdf({ ...state.details, id: ftiId }, action);
    if (state.outputFor === "new_fti") {
      if (locationState?.isNewFti) navigate(pathname, { replace: true });
      getDetails(ftiId, false);
    }
    setOutputFor(null);
  };

  const formatAmount = (amount) =>
    amount === null || amount === undefined
      ? "--"
      : formatCurrency(amount, { currencyPrecision });

  const toggleReissue = (isReissueOpen) => {
    setState((draft) => {
      draft.isReissueOpen = isReissueOpen;
    });
  };

  const handleReissueFti = (payload) => cancelAndReissueFtiApi(ftiId, payload);

  // Exports the re-issued FTI's PDF first, then `isNewFti` asks whether to download or print it
  const handleFtiReissued = async (fti) => {
    await generateFtiPdf(fti.id);
    toggleReissue(false);
    navigate(`${basePath}/full-tax-invoice/${fti.id}/details`, {
      state: { isNewFti: true },
    });
  };

  React.useEffect(() => {
    getDetails(ftiId);
  }, [ftiId]);

  // Arriving from GENERATE (create or re-issue) asks for the first download / print. The PDF
  // is normally exported before navigating; if not (a failed export or a page refresh) it's
  // exported here first, so the dialog is still only shown once the PDF is ready
  React.useEffect(() => {
    if (!locationState?.isNewFti) return;
    const showNewFtiOutput = async () => {
      if (hasGeneratedPdf(ftiId) || (await generateFtiPdf(ftiId))) {
        setOutputFor("new_fti");
      } else {
        navigate(pathname, { replace: true });
      }
    };
    showNewFtiOutput();
  }, [locationState, ftiId]);

  return {
    state,
    isStoreAdmin,
    isEditable,
    breadcrumbs,
    reissueInitialValues,
    reprintRemarks,
    formatAmount,
    isGenerating,
    handlePrint,
    handleConfirmReprint,
    handleOpenReprint: () => toggleReprint(true),
    handleCloseReprint: () => toggleReprint(false),
    handleOpenReissue: () => toggleReissue(true),
    handleCloseReissue: () => toggleReissue(false),
    handleReissueFti,
    handleFtiReissued,
  };
};
